import React, {useEffect, useState} from 'react';
import {
    Box,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Typography,
    TextField,
    MenuItem,
    Checkbox,
    FormControlLabel,
    RadioGroup,
    Radio,
    IconButton
} from '@mui/material';
import {useTheme} from '@mui/material/styles';
import {CurrencySelect, DatePicker, InputField, MoneyField} from '@/pages/components/common';
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

type CellScalar = string | number | boolean | null;

export type EditableDenseTableColumnConfig =
    | { type: 'text'; componentProps?: Partial<React.ComponentProps<typeof InputField>> }
    | {
    type: 'select';
    options: Array<{ label: string; value: string | number }> | ((args: {
        rowIndex: number;
        rows: CellScalar[][]
    }) => Array<{ label: string; value: string | number }>);
    componentProps?: Partial<React.ComponentProps<typeof InputField>>
}
    | { type: 'checkbox'; label?: string; componentProps?: Partial<React.ComponentProps<typeof Checkbox>> }
    | {
    type: 'radio';
    options: Array<{ label: string; value: string | number }>;
    row?: boolean;
    componentProps?: Partial<React.ComponentProps<typeof RadioGroup>>
}
    | { type: 'date'; componentProps?: Partial<React.ComponentProps<typeof DatePicker>> }
    | { type: 'money'; componentProps?: Partial<React.ComponentProps<typeof MoneyField>> }
    | { type: 'currency'; componentProps?: Partial<React.ComponentProps<typeof CurrencySelect>> };

export interface EditableDenseTableProps {
    label?: string;
    head: string[];
    rows: CellScalar[][];
    /** Disable editing globally; by default, table is editable */
    disable?: boolean;
    // Optional external cell change handler; component maintains its own edit buffer.
    onChangeCell?: (rowIndex: number, colIndex: number, value: CellScalar) => void;
    // Optional hook: when a cell changes, you can return additional column updates for the same row.
    // For example, changing a 'userId' select can auto-fill a 'post' column.
    onChangeCellApply?: (rowIndex: number, colIndex: number, value: CellScalar, currentRow: CellScalar[]) => Partial<Record<number, CellScalar>> | void;
    /**
     * Optional callback fired when a row edit is saved, or a new row is added and saved.
     * Provides the full updated rows array for the parent to persist together with other data.
     */
    onRowsChange?: (rows: CellScalar[][]) => void;
    /**
     * If provided, will be called on Save with rows mapped into objects using columnKeys.
     */
    onRowsChangeObjects?: (rows: Record<string, CellScalar>[]) => void;
    /**
     * Column keys mapping used to convert each row array into an object when saving.
     * e.g. ['name','appointmentDate','resignDate']
     */
    columnKeys?: string[];
    /**
     * Optional default values by column key for newly added rows.
     * Example: { isValid: 1 }
     */
    defaultValuesByKey?: Record<string, CellScalar>;
    /**
     * Optional base objects corresponding to current rows, used for merging extra fields
     * that are not part of the editable columns (e.g. immutable metadata per row).
     */
    baseObjects?: Record<string, any>[];
    /**
     * Custom merge strategy applied for each row when mapping arrays -> objects.
     * Defaults to shallow merge: { ...base, ...mapped }.
     */
    rowMerge?: (mapped: Record<string, any>, base: Record<string, any> | undefined, index: number) => Record<string, any>;
    /**
     * Deprecated: use columnConfigs to specify control-specific props.
     */
    cellProps?: (rowIndex: number, colIndex: number) => Partial<React.ComponentProps<typeof TextField>> | undefined;
    /**
     * Optional per-column config to render non-text inputs.
     * If omitted, defaults to TextField for all columns.
     */
    columnConfigs?: EditableDenseTableColumnConfig[];
    /**
     * When this number changes, the table commits all buffered edits and new rows
     * to the parent via onRowsChange / onRowsChangeObjects, then clears buffers.
     */
    commitSignal?: number;
    headerActions?: React.ReactNode;
    rowActions?: (rowIndex: number, row: CellScalar[]) => React.ReactNode;
}

export function EditableDenseTable({
                                       label,
                                       head,
                                       rows,
                                       disable,
                                       onChangeCell,
                                       onChangeCellApply,
                                       onRowsChange,
                                       columnKeys,
                                       defaultValuesByKey,
                                       onRowsChangeObjects,
                                       baseObjects,
                                       rowMerge,
                                       cellProps,
                                       columnConfigs,
                                       commitSignal,
                                       headerActions,
                                       rowActions
                                   }: EditableDenseTableProps) {
    const theme = useTheme();
    // local buffer for edits on existing rows: rowIdx -> values array snapshot being edited
    const [bufferEdits, setBufferEdits] = useState<Record<number, CellScalar[]>>({});
    // newly added rows that don't yet exist in props.rows
    const [newRows, setNewRows] = useState<CellScalar[][]>([]);

    const combinedRows: CellScalar[][] = [...rows, ...newRows];

    // When parent rows change (after save or external update), clear local new/edit buffers
    useEffect(() => {
        setNewRows([]);
        setBufferEdits({});
    }, [rows]);

    // On commit signal, flush all pending edits/new rows to parent and clear buffers
    useEffect(() => {
        if (commitSignal == null) return;
        // Build nextRows like saveEdit would, but committing all at once
        let nextRows = rows.map(r => [...r]);
        // apply all buffered edits across existing rows
        Object.entries(bufferEdits).forEach(([idx, vals]) => {
            const i = Number(idx);
            if (!Number.isNaN(i) && i < nextRows.length) {
                nextRows[i] = [...vals];
            }
        });
        // include all new rows
        nextRows = [...nextRows, ...newRows.map(r => [...r])];
        // Normalize money columns similar to saveEdit
        const moneyCols = (columnConfigs || []).map((c, i) => ({
            c,
            i
        })).filter(x => x.c?.type === 'money').map(x => x.i);
        if (moneyCols.length > 0) {
            const blurFormat = (input: string): string => {
                const raw = (input || '').replace(/,/g, '').trim();
                if (raw === '') return '';
                const n = Number(raw);
                if (!isFinite(n)) return input;
                return n.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2});
            };
            nextRows = nextRows.map(row => {
                const copy = [...row];
                moneyCols.forEach(idx => {
                    const v = copy[idx];
                    if (v == null) return;
                    const s = String(v);
                    copy[idx] = blurFormat(s);
                });
                return copy;
            });
        }
        onRowsChange?.(nextRows);
        if (columnKeys && columnKeys.length > 0 && onRowsChangeObjects) {
            const objects = nextRows.map((arr, i) => {
                const mapped: Record<string, any> = {};
                columnKeys.forEach((k, idx) => {
                    mapped[k] = arr[idx] ?? null;
                });
                const base = baseObjects ? baseObjects[i] : undefined;
                const merged = rowMerge ? rowMerge(mapped, base, i) : {...(base || {}), ...mapped};
                return merged;
            });
            onRowsChangeObjects(objects);
        }
        // clear buffers after commit
        setNewRows([]);
        setBufferEdits({});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [commitSignal]);

    const deleteRow = (rowIdx: number) => {
        if (rowIdx < rows.length) {
            // Remove existing row at index
            const nextRows = rows.filter((_, i) => i !== rowIdx);
            const combined = [...nextRows, ...newRows.map(r => [...r])];
            onRowsChange?.(combined);
            if (columnKeys && onRowsChangeObjects) {
                const objects = combined.map((arr, i) => {
                    const mapped: Record<string, any> = {};
                    columnKeys.forEach((k, idx) => {
                        mapped[k] = arr[idx] ?? null;
                    });
                    const base = baseObjects ? baseObjects[i] : undefined;
                    return rowMerge ? rowMerge(mapped, base, i) : {...(base || {}), ...mapped};
                });
                onRowsChangeObjects(objects);
            }
            // cleanup buffers for shifted indices
            setBufferEdits(prev => {
                const next: Record<number, CellScalar[]> = {};
                Object.entries(prev).forEach(([k, v]) => {
                    const i = Number(k);
                    if (i < rowIdx) next[i] = v;
                    else if (i > rowIdx) next[i - 1] = v;
                });
                return next;
            });
        } else {
            // Remove a new row
            const newIdx = rowIdx - rows.length;
            setNewRows(prev => prev.filter((_, i) => i !== newIdx));
        }
    };

    const handleChangeCell = (rowIdx: number, colIdx: number, value: CellScalar) => {
        // Only update local state while editing; parent receives data on Save via onRowsChange
        if (rowIdx < rows.length) {
            setBufferEdits(prev => {
                const base = prev[rowIdx] ? [...prev[rowIdx]] : [...(rows[rowIdx] || [])];
                base[colIdx] = value;
                if (onChangeCellApply) {
                    const extra = onChangeCellApply(rowIdx, colIdx, value, base);
                    if (extra) {
                        Object.entries(extra).forEach(([k, v]) => {
                            const idx = Number(k);
                            if (!Number.isNaN(idx)) base[idx] = v as any;
                        });
                    }
                }
                return {...prev, [rowIdx]: base};
            });
        } else {
            const newIdx = rowIdx - rows.length;
            setNewRows(prev => {
                const copy = prev.map(r => [...r]);
                if (!copy[newIdx]) return prev;
                copy[newIdx][colIdx] = value;
                if (onChangeCellApply) {
                    const extra = onChangeCellApply(rowIdx, colIdx, value, copy[newIdx]);
                    if (extra) {
                        Object.entries(extra).forEach(([k, v]) => {
                            const idx = Number(k);
                            if (!Number.isNaN(idx)) copy[newIdx][idx] = v as any;
                        });
                    }
                }
                return copy;
            });
        }
        // optional external handler
        onChangeCell?.(rowIdx, colIdx, value);
    };

    const addRow = () => {
        let empty: CellScalar[] = Array.from({length: head.length}, () => '');
        if (columnKeys && defaultValuesByKey) {
            empty = empty.map((v, idx) => {
                const key = columnKeys[idx];
                if (key && Object.prototype.hasOwnProperty.call(defaultValuesByKey, key)) {
                    return defaultValuesByKey[key] as CellScalar;
                }
                return v;
            });
        }
        setNewRows(prev => [...prev, empty]);
    };

    const renderInput = (rowIdx: number, colIdx: number, value: CellScalar, disabled: boolean, extraTFProps?: Partial<React.ComponentProps<typeof TextField>>) => {
        const cfg = columnConfigs && columnConfigs[colIdx];
        const onValChange = (v: CellScalar) => handleChangeCell(rowIdx, colIdx, v);
        if (!cfg || cfg.type === 'text') {
            return (
                <InputField
                    fullWidth
                    align='left'
                    value={value == null ? '' : String(value)}
                    display={disabled}
                    onChange={(e) => onValChange((e.target as HTMLInputElement).value)}
                    {...(cfg && 'componentProps' in cfg ? (cfg as any).componentProps : {})}
                    {...(extraTFProps as any)}
                />
            );
        }
        if (cfg.type === 'money') {
            return (
                <MoneyField
                    align='center'
                    value={value as any}
                    editable={!disabled}
                    onChangeNumber={(s) => onValChange(s)}
                    {...(cfg.componentProps)}
                />
            );
        }
        if (cfg.type === 'currency') {
            return (
                <CurrencySelect
                    value={value as any || 'HKD'}
                    onChange={(code: string) => onValChange(code)}
                    {...(cfg.componentProps)}
                />
            )
        }
        if (cfg.type === 'select') {
            const optList = typeof cfg.options === 'function' ? cfg.options({
                rowIndex: rowIdx,
                rows: combinedRows
            }) : cfg.options;
            return (
                <InputField
                    align='left'
                    fullWidth
                    select
                    display={disabled}
                    value={value == null ? '' : value}
                    onChange={(e) => onValChange((e.target as HTMLInputElement).value)}
                    {...(cfg.componentProps as any)}
                >
                    {optList.map(opt => (
                        <MenuItem dense key={String(opt.value)} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                </InputField>
            );
        }
        if (cfg.type === 'checkbox') {
            return (
                <FormControlLabel
                    control={
                        <Checkbox
                            size="small"
                            disabled={disabled}
                            checked={Boolean(value)}
                            onChange={(e) => onValChange(e.target.checked)}
                            {...cfg.componentProps}
                        />
                    }
                    label={cfg.label || ''}
                />
            );
        }
        if (cfg.type === 'radio') {
            return (
                <RadioGroup
                    row={cfg.row}
                    value={value == null ? '' : value}
                    onChange={(e) => onValChange(e.target.value)}
                    {...cfg.componentProps}
                >
                    {cfg.options.map(opt => (
                        <FormControlLabel key={String(opt.value)} value={opt.value} control={<Radio size="small"/>}
                                          label={opt.label}/>
                    ))}
                </RadioGroup>
            );
        }
        if (cfg.type === 'date') {
            return (
                <DatePicker
                    value={(typeof value === 'string' ? value : null)}
                    onChange={(v) => onValChange(v)}
                    size="small"
                    variant="outlined"
                    display={disabled}
                    fullWidth
                    {...(cfg.componentProps as any)}
                />
            );
        }
        return null;
    };

    // Auto-flush edits and new rows to parent on every buffer change (no explicit Save button)
    useEffect(() => {
        const hasBuffered = Object.keys(bufferEdits).length > 0 || newRows.length > 0;
        if (!hasBuffered) return; // Avoid infinite loops when rows update clears buffers
        // Build nextRows combining existing rows with buffered edits and appended new rows
        let nextRows = rows.map((r, i) => (bufferEdits[i] ? [...bufferEdits[i]] : [...r]));
        nextRows = [...nextRows, ...newRows.map(r => [...r])];
        onRowsChange?.(nextRows);
        if (columnKeys && columnKeys.length > 0 && onRowsChangeObjects) {
            const objects = nextRows.map((arr, i) => {
                const mapped: Record<string, any> = {};
                columnKeys.forEach((k, idx) => {
                    mapped[k] = arr[idx] ?? null;
                });
                const base = baseObjects ? baseObjects[i] : undefined;
                const merged = rowMerge ? rowMerge(mapped, base, i) : {...(base || {}), ...mapped};
                return merged;
            });
            onRowsChangeObjects(objects);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bufferEdits, newRows]);

    return (
        <>
            <Box sx={{display: 'flex', alignItems: 'center', mb: 0.5}}>
                {label ? (
                    <Typography variant="subtitle2" color="text.primary" sx={{fontWeight: 700}}>
                        {label}
                    </Typography>
                ) : null}
                {/*<Box sx={{ ml: 'auto' }}>*/}
                {/*  <Button size="small" variant="outlined" color="primary" onClick={addRow}>Add</Button>*/}
                {/*</Box>*/}
            </Box>
            <Table
                size="small"
                sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    '& .MuiTableCell-root': {py: 0.5, px: 1},
                    '& .MuiTableRow-root': {height: 36},
                    '& thead .MuiTableCell-root': {
                        fontWeight: 700,
                        bgcolor: theme.palette.action.hover,
                        color: theme.palette.text.primary,
                    },
                }}
            >
                <TableHead>
                    <TableRow>
                        {head.map((h, i) => (
                            <TableCell key={i}>{h}</TableCell>
                        ))}
                        <TableCell align="right">
                            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'flex-end'}}>
                                {headerActions}
                                <IconButton size="small" color="primary" onClick={addRow}>
                                    <AddCircleOutlineRoundedIcon fontSize="small"/>
                                </IconButton>
                            </Box>
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {combinedRows.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={head.length + 1} align="center">No data</TableCell>
                        </TableRow>
                    ) : (
                        combinedRows.map((r, rowIdx) => (
                            <TableRow key={rowIdx} hover>
                                {r.map((c, colIdx) => {
                                    const displayVal = (rowIdx < rows.length && bufferEdits[rowIdx]
                                        ? bufferEdits[rowIdx][colIdx]
                                        : c);
                                    const value = displayVal as CellScalar;
                                    const extra = cellProps ? (cellProps(rowIdx, colIdx) || {}) : {};
                                    const disabled = (extra as any).disabled ?? Boolean(disable);
                                    return (
                                        <TableCell key={colIdx}>
                                            {renderInput(rowIdx, colIdx, value, disabled, extra)}
                                        </TableCell>
                                    );
                                })}
                                <TableCell align="right" sx={{whiteSpace: 'nowrap'}}>
                                    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'flex-end'}}>
                                        {rowActions
                                            ? rowActions(
                                                rowIdx,
                                                (rowIdx < rows.length
                                                    ? (bufferEdits[rowIdx] ? bufferEdits[rowIdx] : rows[rowIdx])
                                                    : (newRows[rowIdx - rows.length] || r)) as CellScalar[]
                                            )
                                            : null}
                                        <IconButton size="small" color="error" onClick={() => deleteRow(rowIdx)}>
                                            <DeleteOutlineIcon fontSize="small"/>
                                        </IconButton>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
        </>
    );
};
